import React, { memo, useCallback } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ReplayIcon from '@mui/icons-material/Replay';
import { Chip, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import useAppNavigation from '~/hooks/useAppNavigation';

import {
  HERO_PLANNER_DEBUG_DEFAULT_LOYALTY_ID,
  HeroPlannerDebugRow,
  getHeroPlannerDebugMatchedIdentifier,
  isSameHeroPlannerDebugRow,
} from '../../utils/heroPlannerDebugUtils';
import { getHeroPlannerFormType } from '../../utils/heroPlannerScheduleUtils';
import { SCHEDULE_SEARCH_PARAMS, isCountryStateFilterSupportedForBrand } from '../SchedulesFilters';

import HeroPlannerDebugResultDetails from './HeroPlannerDebugResultDetails';
import HeroPlannerDebugStatusChip from './HeroPlannerDebugStatusChip';

const MIDDLE_MOUSE_BUTTON = 1;

interface Props {
  row: HeroPlannerDebugRow;
  brand: string;
  /**
   * YYYY-MM-DD
   */
  sendDate: string;
  offerId: string;
  onViewResponse: (row: HeroPlannerDebugRow) => void;
  onCopyUrl: (url: string) => void;
  onRetry: (targetKey: string) => void;
}

/**
 * Where clicking a segment goes: the hero planner row its schedule is built from, or - when there isn't one - a new
 * send pre-filled for that segment's date and location so the gap can be filled.
 */
function getSegmentLink(
  row: HeroPlannerDebugRow,
  brand: string,
  sendDate: string,
): { to: string; title: string } | undefined {
  const { heroRow, target, status } = row;

  if (heroRow) {
    return {
      to: `/customer-communication/hero-planner/schedules/${heroRow.id}?formType=${getHeroPlannerFormType(heroRow.segmentId, heroRow.brandId)}`,
      title: 'Open the hero planner row this segment uses',
    };
  }

  if (status === 'failed') {
    const params = new URLSearchParams();
    params.set(SCHEDULE_SEARCH_PARAMS.FROM_DATE, sendDate);
    params.set(SCHEDULE_SEARCH_PARAMS.COUNTRY_GROUP, target.countryGroup);
    params.set(SCHEDULE_SEARCH_PARAMS.COUNTRY, target.country);
    if (isCountryStateFilterSupportedForBrand(brand)) {
      params.set(SCHEDULE_SEARCH_PARAMS.COUNTRY_STATE, target.state);
    }
    return {
      to: `/customer-communication/hero-planner/schedules/new?${params.toString()}`,
      title: 'No hero planner row for this segment - create a send for it',
    };
  }

  return undefined;
}

function HeroPlannerDebugResultRow({ row, brand, sendDate, offerId, onViewResponse, onCopyUrl, onRetry }: Props) {
  const { target, outcome, fallbackFields, offerSlots, status } = row;
  const segmentLink = getSegmentLink(row, brand, sendDate);
  const hasMatchedRow = status === 'ok' || status === 'heroOnly' || (status === 'regenerating' && !!row.heroRow);
  const { push } = useAppNavigation();
  const linkTo = segmentLink?.to;

  const handleRowClick = useCallback(
    (event: React.MouseEvent) => {
      // let people select and copy text (e.g. an error message) without navigating away
      if (!linkTo || window.getSelection()?.toString()) {
        return;
      }
      // cmd/ctrl click opens a new tab like a normal link would
      if (event.metaKey || event.ctrlKey) {
        window.open(linkTo, '_blank', 'noopener');
        return;
      }
      push(linkTo);
    },
    [linkTo, push],
  );

  // auxclick fires for every non-primary button, so only treat the middle button as "open in a new tab" and leave
  // right clicks to the context menu
  const handleRowAuxClick = useCallback(
    (event: React.MouseEvent) => {
      if (linkTo && event.button === MIDDLE_MOUSE_BUTTON) {
        window.open(linkTo, '_blank', 'noopener');
      }
    },
    [linkTo],
  );

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // only when the row itself is focused, not a button inside it
      if (linkTo && event.key === 'Enter' && event.target === event.currentTarget) {
        push(linkTo);
      }
    },
    [linkTo, push],
  );

  // the row is clickable, so stop the action buttons' clicks from also triggering it
  const stopRowClick = useCallback((event: React.SyntheticEvent) => event.stopPropagation(), []);

  return (
    <TableRow
      hover
      onClick={linkTo ? handleRowClick : undefined}
      onAuxClick={linkTo ? handleRowAuxClick : undefined}
      onKeyDown={linkTo ? handleRowKeyDown : undefined}
      tabIndex={linkTo ? 0 : undefined}
      role={linkTo ? 'link' : undefined}
      aria-label={segmentLink?.title}
      title={segmentLink?.title}
      sx={(theme) => ({
        verticalAlign: 'top',
        ...(linkTo && {
          cursor: 'pointer',
          '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
        }),
        ...(status === 'failed' && { bgcolor: alpha(theme.palette.error.main, 0.06) }),
        ...(status === 'regenerating' && { bgcolor: alpha(theme.palette.warning.main, 0.08) }),
        ...(status === 'skipped' && { opacity: 0.6 }),
        ...(status !== 'failed' && offerSlots.length > 0 && { bgcolor: alpha(theme.palette.info.main, 0.08) }),
      })}
    >
      <TableCell>
        <HeroPlannerDebugStatusChip status={status} outcome={outcome} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {target.countryName}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {target.country}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{target.stateName}</Typography>
        <Typography variant="caption" color="text.secondary">
          {target.cityName}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" fontWeight="medium">
            {target.segmentName}
          </Typography>
          {target.loyalty !== HERO_PLANNER_DEBUG_DEFAULT_LOYALTY_ID && (
            <Chip size="small" color="secondary" label={target.loyaltyName} />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {target.segment}
        </Typography>
      </TableCell>
      <TableCell>
        {hasMatchedRow && (
          <Tooltip
            title={
              <Typography variant="caption" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {getHeroPlannerDebugMatchedIdentifier(row)}
              </Typography>
            }
          >
            {fallbackFields.length === 0 ? (
              <Chip size="small" variant="outlined" color="success" label="Exact match" />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={`Fell back on ${fallbackFields.join(', ')}`}
              />
            )}
          </Tooltip>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 320 }}>
        <HeroPlannerDebugResultDetails row={row} offerId={offerId} />
      </TableCell>
      <TableCell align="right">
        <Typography variant="caption" color="text.secondary" noWrap>
          {outcome ? `${outcome.durationMs}ms` : ''}
        </Typography>
      </TableCell>
      <TableCell align="right" onClick={stopRowClick} onAuxClick={stopRowClick} sx={{ cursor: 'default' }}>
        <Stack direction="row" justifyContent="flex-end">
          <Tooltip title="View response">
            <span>
              <IconButton size="small" disabled={!outcome} onClick={() => onViewResponse(row)}>
                <DataObjectIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Copy request URL">
            <span>
              <IconButton size="small" disabled={!outcome} onClick={() => outcome && onCopyUrl(outcome.url)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Re-check this segment">
            <span>
              <IconButton
                size="small"
                disabled={status === 'loading' || status === 'skipped'}
                onClick={() => onRetry(target.key)}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

// a sweep re-renders the page every time a result lands, so only re-render the rows whose result changed
export default memo(
  HeroPlannerDebugResultRow,
  (prev, next) =>
    prev.brand === next.brand &&
    prev.sendDate === next.sendDate &&
    prev.offerId === next.offerId &&
    prev.onViewResponse === next.onViewResponse &&
    prev.onCopyUrl === next.onCopyUrl &&
    prev.onRetry === next.onRetry &&
    isSameHeroPlannerDebugRow(prev.row, next.row),
);
